<template>
  <div class="registration-form">
    <slot name="header" v-if="!submitted">
      <div class="registration-form__header">
        <span>RSVP for Goldman Sachs’ Communicating with Impact Workshop</span>
        <v-btn class="close" elevation="0" @click="close" icon>
          <v-icon>$close</v-icon>
        </v-btn>
        <div class="page-intro">
          <p class="page-intro">
            Join Goldman Sachs for an in-depth workshop on how to communicate
            effectively!
          </p>
          <p class="page-intro">
            The Communicating with Impact session will cover two main topics:
            <ol>
              <li>Best practices for communicating information—including tips on
            content delivery and self-delivery during presentations</li>
              <li>Background and tools to overcome Imposter Syndrome</li>
            </ol>
          </p>
          <p class="page-intro">
            This workshop will be held over Zoom on Thursday June 24 from
            3:00-4:00 pm EST.
          </p>
        </div>
      </div>
    </slot>
    <v-form
      lazy-validation
      v-model="isFormValid"
      ref="form"
      @submit.prevent="submit"
      v-if="!submitted"
    >
      <v-layout :wrap="['sm', 'xs'].includes($size)">
        <v-flex xs12 sm12 md6>
          <v-text-field
            v-model="form.first_name"
            :rules="[validation.required]"
            label="First name"
            validate-on-blur
          ></v-text-field>
        </v-flex>
        <v-flex class="px-3"></v-flex>
        <v-flex xs12 sm12 md6>
          <v-text-field
            v-model="form.last_name"
            :rules="[validation.required]"
            label="Last name"
            validate-on-blur
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout :wrap="['sm', 'xs'].includes($size)">
        <v-flex xs12 sm12 md6>
          <v-text-field
            v-model="form.email"
            :rules="[validation.required, validation.email]"
            type="email"
            label="Email address"
            validate-on-blur
          ></v-text-field>
        </v-flex>
        <v-flex class="px-3"></v-flex>
        <v-flex xs12 sm12 md6>
          <v-text-field
            label="Phone number (optional)"
            v-model="form.phone"
            type="text"
            :rules="[validations.onboarding.phone]"
            v-mask="'###-###-####'"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout :wrap="['sm', 'xs'].includes($size)">
        <v-flex xs12 sm12 md6>
          <v-text-field
            v-model="form.school"
            :rules="[validation.required]"
            label="School"
            type="text"
            validate-on-blur
          ></v-text-field>
        </v-flex>
      </v-layout>
      <div class="registration-form__radio-group">
        <div>
          How did you hear about this workshop?
        </div>
        <v-radio-group v-model="form.how_did_you_hear" 
          :rules="[validation.required]"
          validate-on-blur
        >
          <v-radio
            v-for="option in howDidYouHearOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          ></v-radio>
        </v-radio-group>
      </div>
      <div class="registration-form__radio-group">
        <div>
          Which degree are you pursuing or have you already earned?
        </div>
        <v-radio-group v-model="form.degree" 
          :rules="[validation.required]"
          validate-on-blur
        >
          <v-radio
            v-for="option in degreeOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          ></v-radio>
        </v-radio-group>
      </div>
      <div class="registration-form__radio-group">
        <div>
          What is your class standing?
        </div>
        <v-radio-group v-model="form.standing" 
          :rules="[validation.required]"
          validate-on-blur
        >
          <v-radio
            v-for="option in standingOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          ></v-radio>
        </v-radio-group>
      </div>
      <v-layout :wrap="['sm', 'xs'].includes($size)">
        <v-flex xs12 sm12 md6>
          <v-text-field
            v-model="form.movie_titles"
            label="CMF Movie Title(s)"
            type="text"
            validate-on-blur
          ></v-text-field>
        </v-flex>
      </v-layout>
      <br />
      <br />
      <v-btn
        :disabled="loading"
        x-large
        type="submit"
        color="primary"
        elevation="0"
        >{{ loading ? "Processing..." : "Submit" }}</v-btn
      >
      <br />
      <br />
      <Recaptcha />
    </v-form>
    <div v-else class="pa-8 text-center">
      <v-btn class="close" @click="close" icon elevation="0">
        <v-icon>$close</v-icon>
      </v-btn>
      <div style="margin: auto auto 4em">
        <div class="h2">
          Thank you! Your registration has been recieved.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import formMixin from "@/mixins/form.mixin";
import validations from "@/data/validations";

export default {
  name: "RegistrationPage",
  metaInfo: {
    titleTemplate: "%s - Registration"
  },
  extends: formMixin,
  data() {
    return {
      loading: false,
      validations,
      submitted: false,
      eventName: "Goldman Sachs’ Communicating with Impact Workshop",
      form: {},
      howDidYouHearOptions: [
        {
          value: "email",
          label: "Email"
        },
        {
          value: "social_media",
          label: "Social Media"
        },
        {
          value: "school_communication",
          label: "School Communication"
        },
        {
          value: "friend",
          label: "Friend"
        },
      ],
      degreeOptions: [
        {
          value: "associate",
          label: "Associate"
        },
        {
          value: "bachelors",
          label: "Bachelors"
        },
        {
          value: "masters",
          label: "Masters"
        },
        {
          value: "doctorate",
          label: "Doctorate"
        },
      ],
      standingOptions: [
        {
          value: "freshman",
          label: "Freshman"
        },
        {
          value: "sophomore",
          label: "Sophomore"
        },
        {
          value: "junior",
          label: "Junior"
        },
        {
          value: "senior",
          label: "Senior"
        },
        {
          value: "postbaccalaureate",
          label: "Postbaccalaureate"
        },
        {
          value: "n_a",
          label: "N/A"
        },
      ]
    };
  },
  created() {
    this.reset();
  },
  methods: {
    async submit() {
      try {
        this.loading = true;
        if (!this.validate()) return;
        await this.$recaptchaLoaded();
        const recaptcha = await this.$recaptcha("login");

        await this.$api.RegistrationRequest.create({
          request: this.form,
          recaptcha
        });
        // this.form = {};
        this.loading = false;
        //this.$router.push("/");
        this.submitted = true;
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.reset();
      this.$emit("close");
      this.submitted = false;
    },
    reset() {
      this.form = {
        first_name: this.currentUser?.first_name || "",
        last_name: this.currentUser?.last_name || "",
        email: this.currentUser?.email || "",
        event_name: this.eventName
      };
    }
  }
};
</script>

<style scoped lang="scss">
.registration-form {
  color: $cmf-darkgrey;
  position: relative;

  p {
    font-size: 20px;
  }

  form {
    padding: 0 120px 50px;
  }

  &__header {
    display: flex;
    align-items: center;
    flex-direction: column;
    padding: 80px 120px 25px;
    font-weight: 300;
    font-size: 36px;
    line-height: 45px;
    letter-spacing: -0.02em;
  }

  &__radio-group {
    margin-top: 2em;
  }
}
.v-btn--outlined .v-btn__content .v-icon,
.v-btn--round .v-btn__content .v-icon {
  &::v-deep {
    color: $primary;
  }
}
</style>
